/* eslint-disable react/no-unused-class-component-methods */
import {formattedPropertyModel, utils} from '@wallaby-ui/core';
import {isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {Form} from 'react-bootstrap';

import GridFilterWrapper from './GridFilterWrapper';
import styles from './GridTextFilter.less';
import {DynamicFilter} from 'src/js/lib/filtering/filters_definitions';
import {FILTER_TYPE_TEXT} from 'src/js/lib/filtering/filters_definitions_constants';
import {
  CellRendererParams,
  CELL_RENDERER_PARAMS,
  CELL_RENDERER_PARAMS_AG,
} from 'src/js/model/cell_renderer_params';

const STATIC_EMPTY_OBJECT = {};

class GridTextFilter extends React.PureComponent {
  static matchFilter(filterWord, nodeValue) {
    return (
      utils.StringUtils.stripDiacritics(
        nodeValue.toString().toLowerCase()
      ).indexOf(filterWord) >= 0
    );
  }

  constructor(props) {
    super(props);

    const text = '';
    this.state = {
      // model that is not changed, unless the filter change is finally submitted
      loadedModel: undefined,
      // model that is changed with each filter change
      currentModel: GridTextFilter.getFilter({text}, props),
      text,
    };
    const {colDef, valueGetter} = this.props;
    this.valueGetter = valueGetter;
    this.translateService = null;

    const formatterParams = new CellRendererParams(
      colDef[CELL_RENDERER_PARAMS_AG] || colDef[CELL_RENDERER_PARAMS] || {}
    );
    this.i18nParams = formatterParams.getI18nParams();

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(event) {
    if (!this.isFilterEmpty()) {
      if (event.key === 'Enter') {
        event.stopPropagation();
        this.onSubmit();
      }
    }
    return true;
  }

  onChange(event) {
    const {filterModifiedCallback} = this.props;
    const {text} = this.state;
    const newValue = event.target.value;
    if (text !== newValue) {
      this.setState(
        {
          text: newValue,
        },
        () => {
          // getFilter depends on state, it has to be called in callback
          this.setState(
            (state, props) => ({
              currentModel: GridTextFilter.getFilter(state, props),
            }),
            () => {
              filterModifiedCallback();
            }
          );
        }
      );
    }
  }

  onSubmit() {
    const {filterChangedCallback} = this.props;
    this.setState(
      (state) => ({
        loadedModel: state.currentModel,
      }),
      () => {
        filterChangedCallback();

        if (this.hidePopUp) {
          this.hidePopUp();
        }
      }
    );
  }

  static getFilter(state, props) {
    const TextFilter = DynamicFilter.getFilterClass(FILTER_TYPE_TEXT);
    return new TextFilter({
      type: FILTER_TYPE_TEXT,
      property: props.colDef.field,
      value: state.text,
    });
  }

  getModel() {
    const {currentModel} = this.state;
    return this.isFilterEmpty() ? null : currentModel;
  }

  setModel(model) {
    this.setState({
      text: model && model.getValue ? model.getValue() : '',
      loadedModel: model || STATIC_EMPTY_OBJECT,
      currentModel: model || STATIC_EMPTY_OBJECT,
    });
  }

  isFilterActive() {
    const {loadedModel} = this.state;
    return !isEmpty(loadedModel);
  }

  isFilterEmpty() {
    const {text} = this.state;
    return isEmpty(text);
  }

  removeFilter() {
    const {filterChangedCallback} = this.props;
    this.setState(
      {
        loadedModel: undefined,
        text: '',
      },
      () => {
        // getFilter depends on state, it has to be called in callback
        this.setState(
          (state, props) => ({
            currentModel: GridTextFilter.getFilter(state, props),
          }),
          () => {
            filterChangedCallback();
            if (this.hidePopUp) {
              this.hidePopUp();
            }
          }
        );
      }
    );
  }

  doesFilterPass(params) {
    const {text} = this.state;
    const nodeValue = this.valueGetter(params.node);
    let translatedValue;
    if (
      this.translateService &&
      utils.TranslateUtils.hasTranslation(nodeValue, this.translateService)
    ) {
      translatedValue = formattedPropertyModel.FormattedProperty.formatLabel(
        nodeValue,
        this.translateService,
        params.data,
        this.i18nParams
      );
    }
    return utils.StringUtils.stripDiacritics(text.toLowerCase())
      .split(' ')
      .every((filterWord) =>
        translatedValue
          ? GridTextFilter.matchFilter(filterWord, translatedValue)
          : GridTextFilter.matchFilter(filterWord, nodeValue)
      );
  }

  afterGuiAttached(params) {
    this.focus();
    this.hidePopUp = params.hidePopup;
  }

  focus() {
    setTimeout(() => {
      if (this.elem) {
        this.elem.focus();
      }
    });
  }

  render() {
    const {colDef} = this.props;
    const {currentModel, loadedModel, text} = this.state;
    const filter = currentModel;
    return (
      <div className={styles.dlGridTextFilter}>
        <GridFilterWrapper
          filter={filter}
          isActive={this.isFilterActive()}
          isEmpty={this.isFilterEmpty()}
          label={colDef.headerName}
          loadedModel={loadedModel}
          removeFilter={this.removeFilter}
          submitFilter={this.onSubmit}
        >
          {(intl) => {
            this.translateService = intl;
            return (
              <Form.Control
                ref={(elem) => {
                  this.elem = elem;
                }}
                as='input'
                tabIndex={0}
                type='text'
                size='sm'
                className={styles.dlGridTextFilterInput}
                value={text}
                onChange={this.onChange}
                onKeyDown={this.handleKeyDown}
              />
            );
          }}
        </GridFilterWrapper>
      </div>
    );
  }
}

GridTextFilter.displayName = 'GridTextFilter';

GridTextFilter.propTypes = {
  colDef: PropTypes.objectOf(PropTypes.any).isRequired,
  filterChangedCallback: PropTypes.func.isRequired,
  filterModifiedCallback: PropTypes.func.isRequired,
  valueGetter: PropTypes.func.isRequired,
};

export default GridTextFilter;
